import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, Flame } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { Skeleton } from '../components/ui/skeleton';
import SEO from '../utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const TrendingPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Trending Posters | PosterFire — Hot Right Now';
    SEO.setCanonical(`${SEO.CANONICAL_BASE}/trending`);
    SEO.setRobots('index, follow');
    SEO.setMeta('meta[name="description"]', 'content', 'Shop the hottest trending posters at PosterFire. See what everyone is buying — movies, music, sports & more.');
    return () => SEO.resetDefaults();
  }, []);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        const response = await axios.get(`${API}/products/trending/list?limit=60`);
        setProducts(shuffle(response.data));
      } catch (error) {
        console.error('Failed to fetch trending:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrending();
  }, []);

  return (
    <div className="min-h-screen fire-gradient" data-testid="trending-page">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Flame className="w-8 h-8 text-fire" />
            <h1 className="font-heading text-4xl md:text-5xl text-white drop-shadow-lg">
              Trending Now
            </h1>
          </div>
          <p className="text-white/70">Hot picks and best sellers — refreshed every visit</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="aspect-[3/4] w-full bg-white/10" />
                <Skeleton className="h-3 w-16 bg-white/10" />
                <Skeleton className="h-4 w-full bg-white/10" />
                <Skeleton className="h-4 w-12 bg-white/10" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-black/30 rounded">
            <TrendingUp className="w-16 h-16 mx-auto text-white/30 mb-4" />
            <h3 className="font-heading text-2xl text-white mb-4">No Trending Products Yet</h3>
            <p className="text-white/60 mb-8">Check back soon for our hottest picks!</p>
            <Link to="/" className="px-6 py-3 bg-fire text-white font-bold uppercase tracking-wider hover:bg-fire-light transition-colors">
              Browse All Posters
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="trending-grid">
            {products.map((product, i) => (
              <div key={product.id} className="relative animate-fade-in" style={{ animationDelay: `${i * 0.02}s` }}>
                {product.trending && (
                  <div className="absolute top-2 left-2 z-10 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-bold uppercase px-2 py-1 flex items-center gap-1">
                    <Flame className="w-3 h-3" /> Hot
                  </div>
                )}
                <ProductCard product={product} index={i} />
              </div>
            ))}
          </div>
        )}

        {!loading && products.length > 0 && (
          <div className="text-center mt-8 text-white/50 text-sm">
            Showing {products.length} trending posters
          </div>
        )}
      </div>
    </div>
  );
};

export default TrendingPage;
